import { useEffect, useRef, useState, ReactNode} from 'react';
import type { RefObject } from 'react';
import { Box, Collapse, Stack, Typography, useTheme } from '@mui/material';
import { Button, Input } from '@repo/ui';
import { Contact, IdCard } from 'lucide-react';
import { fieldFocusSx } from 'components/UI/fieldMetrics';
import { isContactPickerSupported, pickContact } from 'utils/contactPicker';
import { VCARD_MAX_BYTES, isVCardFile, parseVCard } from 'utils/vcard';
import { isEmailValid } from 'utils/parsers';

/**
 * VRG-720 -- the visitor's name + email, as one reusable unit.
 *
 * Built as its own component (not inline in `RequestAccessInfo`) because the same two
 * fields, the same autofill behaviour and the same "Use my contact card" shortcut are needed
 * by slice 2's claim-ownership drawer. Whatever hosts it owns the state, the submit button
 * and what happens next; this owns only how a person hands over who they are.
 *
 * ## How a visitor fills it, best path first
 *
 *  1. Autofill. `autoComplete="name"` / `"email"` plus matching `name` attributes make iOS
 *     offer the contact-card chip above the keyboard and Chrome/1Password offer their stored
 *     details. This is the ONLY one-tap path on iPhone, where the Contact Picker does not
 *     exist.
 *  2. "Use my contact card" -- the Web Contact Picker, rendered ONLY where the browser has it
 *     (Chrome on Android). Where it is absent the button does not render at all, so nothing
 *     on iPhone points at a capability it lacks.
 *  3. "Share contact card" -- a `.vcf` the visitor picks from Files (VRG-720). The only
 *     path that works EVERYWHERE and the only one that can carry company, job title and a
 *     work email: the Contact Picker's property list is name/email/tel/address/icon and has
 *     no organization at all, on any platform. On iPhone the route is Contacts -> share the
 *     card -> Save to Files -> pick it here.
 *  4. Typing.
 *
 * ## Why this is INLINE and not a modal over the gate
 *
 * It was proposed as a pop-up that appears with the access gate ("share vCard instead").
 * Rejected, and the reason is the arithmetic rather than taste: the gate is ALREADY an
 * interruption -- a stranger tapped a tag and is being asked for an email before they may
 * look at what they tapped -- so a modal on top of it interrupts an interruption, and
 * everyone who intends to type (the majority, since the .vcf route on iOS is Contacts ->
 * share -> Save to Files -> come back) pays a dismissal for a path they did not want. It
 * would also be a third stacked layer on a surface that already has drawers, which
 * working-conventions section 52 rules out outright. Inline costs nothing to ignore and is
 * equally discoverable, sitting directly above the fields it fills.
 *
 * ## Validity
 *
 * `isContactDetailsValid` is the one predicate. The host uses it to disable its submit
 * button, and this component uses the same rules to decide what error to show, so the two
 * cannot disagree about whether the form is complete. Errors appear once a field has been
 * left (or after a failed attempt), not while the visitor is still typing the first
 * character.
 *
 * ## Autofill that React never hears about
 *
 * Safari (esp. iOS) can populate a field visually without firing a change event, leaving
 * the host's state empty while the DOM holds a value. With a submit button that is disabled
 * until valid, that would be a dead button on exactly the path this form exists to make
 * easy. So the component re-reads the DOM values on a short interval and on `change` and
 * pushes any difference into state. 400ms is cheap for a form that lives for seconds.
 */

export const CONTACT_NAME_MAX_LENGTH = 200;
/*
 * Mirrors the API DTO's `@MaxLength` on each field. A `maxLength` here is a
 * courtesy that stops the visitor typing past the bound; the DTO is the control.
 * They are stated as constants so the two can be compared at a glance rather
 * than by reading two files.
 */
export const CONTACT_COMPANY_MAX_LENGTH = 200;
export const CONTACT_TITLE_MAX_LENGTH = 200;
export const CONTACT_PHONE_MAX_LENGTH = 50;

export const getNameError = (name: string): string => {
  const value = name.trim();
  if (!value) return 'Name is required';
  if (value.length > CONTACT_NAME_MAX_LENGTH) return 'Name is too long';
  return '';
};

export const getEmailError = (email: string): string => {
  const value = email.trim();
  if (!value) return 'Email is required';
  if (!isEmailValid(value)) return 'Please enter a valid email address';
  return '';
};

export const isContactDetailsValid = (name: string, email: string): boolean =>
  !getNameError(name) && !getEmailError(email);

interface ContactDetailsFieldsProps {
  name: string;
  email: string;
  onNameChange: (value: string) => void;
  onEmailChange: (value: string) => void;
  /** The hosts read these on submit as a fallback for autofill that skipped React. */
  nameInputRef: RefObject<HTMLInputElement>;
  emailInputRef: RefObject<HTMLInputElement>;
  /** Show errors for empty/invalid fields even if untouched (set after a failed attempt). */
  showErrors?: boolean;
  disabled?: boolean;
  /** Render the Contact Picker shortcut where the browser supports it. Default true. */
  allowContactPicker?: boolean;
  /**
   * Further ONE-TAP ways to supply the same name + email, rendered with this
   * component's own contact-card buttons above the divider — today the host's
   * "Continue with Google".
   *
   * It is a slot rather than a prop because the host owns what goes in it: the
   * Google button needs a company id, a redirect handler and the host's submitting
   * state, none of which this component has any business knowing. What this
   * component DOES own is the ORDER — every one-tap path together, then the
   * divider, then the manual fields — and that ordering is the entire point of the
   * slot. A host rendering its own quick action outside the component puts it on
   * the wrong side of the divider, which is exactly the layout this replaced.
   */
  quickActions?: ReactNode;
  /*
   * VRG-720 — the contact card's other three fields.
   *
   * OPTIONAL PROPS, not required ones: `ClaimOnScanCard` and `RequestAccessInfo`
   * are not the only conceivable hosts, and a host that has nowhere to send them
   * should not be forced to thread three no-op handlers. When the setters are
   * absent the section does not render at all, so a host cannot accidentally
   * show fields whose values it then drops — which is the exact failure this
   * whole change is undoing.
   */
  company?: string;
  title?: string;
  phone?: string;
  onCompanyChange?: (value: string) => void;
  onTitleChange?: (value: string) => void;
  onPhoneChange?: (value: string) => void;
}

export const ContactDetailsFields = ({
  name,
  email,
  onNameChange,
  onEmailChange,
  nameInputRef,
  emailInputRef,
  showErrors = false,
  disabled = false,
  allowContactPicker = true,
  quickActions,
  company = '',
  title = '',
  phone = '',
  onCompanyChange,
  onTitleChange,
  onPhoneChange,
}: ContactDetailsFieldsProps) => {
  // Resolved here rather than via an `sx` callback: `@repo/ui`'s Input merges `sx` with an
  // object spread, so a `theme => ...` form spreads to `{}` and is silently dropped.
  const theme = useTheme();
  const [nameTouched, setNameTouched] = useState(false);
  const [emailTouched, setEmailTouched] = useState(false);
  const [pickerSupported] = useState(() => allowContactPicker && isContactPickerSupported());

  /*
   * THE OPTIONAL SECTION IS HIDDEN UNTIL THE FORM KNOWS WHO YOU ARE.
   *
   * This gate is a friction point on the way to something the visitor already
   * tapped a physical tag to see, so the cost of a field is not its height alone
   * -- it is the abandonment that five fields buy over two. Hidden until there is
   * a name means the extra ask only reaches someone who has already committed
   * something, and the fold stays short for everyone who has not.
   *
   * Two independent triggers, and the second is the one that matters:
   *
   *   1. a name exists -- typed, autofilled or filled from a card. "Typed
   *      MANUALLY" was the brief, but keying on that alone would hide the section
   *      from a visitor whose browser autofilled the name for them, who is the
   *      same person one step further along.
   *   2. any of the three already has a VALUE -- the shared-card path. A card
   *      that carried an employer must show it, because the visitor is about to
   *      send it and has not yet seen it. Revealing it filled-in is what makes it
   *      reviewable and correctable rather than silently submitted.
   *
   * Never collapses once open: a section that reappears and vanishes as the name
   * field is cleared and retyped is worse than either state.
   */
  const hasOptionalValue = Boolean(company.trim() || title.trim() || phone.trim());
  const optionalSupported = Boolean(onCompanyChange || onTitleChange || onPhoneChange);
  const [optionalRevealed, setOptionalRevealed] = useState(
    () => optionalSupported && (Boolean(name.trim()) || hasOptionalValue),
  );
  useEffect(() => {
    if (!optionalSupported) return;
    if (name.trim() || hasOptionalValue) setOptionalRevealed(true);
  }, [name, hasOptionalValue, optionalSupported]);

  // Autofill that never reached React (see the note above).
  useEffect(() => {
    const sync = () => {
      const domName = nameInputRef.current?.value;
      if (domName !== undefined && domName !== name) onNameChange(domName);
      const domEmail = emailInputRef.current?.value;
      if (domEmail !== undefined && domEmail !== email) onEmailChange(domEmail);
    };
    const interval = window.setInterval(sync, 400);
    const nameEl = nameInputRef.current;
    const emailEl = emailInputRef.current;
    nameEl?.addEventListener('change', sync);
    emailEl?.addEventListener('change', sync);
    return () => {
      window.clearInterval(interval);
      nameEl?.removeEventListener('change', sync);
      emailEl?.removeEventListener('change', sync);
    };
  }, [name, email, onNameChange, onEmailChange, nameInputRef, emailInputRef]);

  const vcardInputRef = useRef<HTMLInputElement>(null);
  const [vcardError, setVcardError] = useState<string | null>(null);

  /*
   * VRG-720 -- read a shared `.vcf` and fill what the form has fields for.
   *
   * `company`, `title` and `phone` now LAND, in the optional section below. They used to
   * be parsed and dropped on the floor, because `scans` held `contact_name` and `email`
   * and nothing else -- wiring a field to no column would have looked like capture and
   * silently discarded, the half-wired shape working-conventions section 30 calls out.
   * Migration 0179 adds `contact_company` / `contact_title` / `contact_phone`, so the
   * parser's output has somewhere to go; the parser itself needed no change for it.
   *
   * They are revealed FILLED rather than submitted invisibly. A card carries an employer
   * and a personal phone number, and a visitor who taps "Share contact card" has not seen
   * what the card contains at the moment they tap it. Showing the parsed values is what
   * makes the share reviewable -- and correctable -- instead of a silent upload.
   */
  const handleVCardFile = async (file: File | undefined) => {
    setVcardError(null);
    if (!file) return;
    // iOS sets neither a reliable extension nor a MIME type on everything, so this is a
    // nudge rather than a gate -- a file that slips through simply fails to parse below.
    if (!isVCardFile(file) && file.size > VCARD_MAX_BYTES) {
      setVcardError('That does not look like a contact card.');
      return;
    }
    if (file.size > VCARD_MAX_BYTES) {
      setVcardError('That file is too large to be a contact card.');
      return;
    }
    let parsed = null;
    try {
      parsed = parseVCard(await file.text());
    } catch {
      parsed = null;
    }
    if (!parsed) {
      setVcardError('We could not read that contact card. Try again, or type your details.');
      return;
    }
    if (parsed.fullName) onNameChange(parsed.fullName);
    if (parsed.email) onEmailChange(parsed.email);
    // Only overwrite with something: a card with no ORG must not wipe a company the
    // visitor has already typed by hand.
    if (parsed.company && onCompanyChange) onCompanyChange(parsed.company);
    if (parsed.title && onTitleChange) onTitleChange(parsed.title);
    if (parsed.phone && onPhoneChange) onPhoneChange(parsed.phone);
    // A card with no email still filled the name, so say what is still needed rather than
    // leaving the visitor to notice an empty field for themselves.
    if (!parsed.email) {
      setVcardError('That card has no email address. Please add one below.');
    }
    setNameTouched(true);
    setEmailTouched(true);
  };

  const handlePickContact = async () => {
    const picked = await pickContact();
    if (!picked) return;
    if (picked.name) onNameChange(picked.name);
    if (picked.email) onEmailChange(picked.email);
    // Whatever the card did not supply is still required, so it shows its error.
    setNameTouched(true);
    setEmailTouched(true);
  };

  const nameError = nameTouched || showErrors ? getNameError(name) : '';
  const emailError = emailTouched || showErrors ? getEmailError(email) : '';

  return (
    /* GAP 12px on apps/app's 8px base is `1.5` (working-conventions section 29). */
    <Stack spacing={1.5}>
      {/* Hidden: the Button above it is the affordance. `accept` lists both the extension
          and every MIME type the platforms use, because iOS and Android disagree. */}
      <input
        ref={vcardInputRef}
        type='file'
        accept='.vcf,text/vcard,text/x-vcard,text/directory'
        style={{ display: 'none' }}
        onChange={event => {
          void handleVCardFile(event.target.files?.[0]);
          // Reset so picking the SAME file twice still fires a change event -- otherwise a
          // visitor who picks the wrong card, then the right one, then the first again gets
          // nothing on the third try.
          event.target.value = '';
        }}
      />
      <Button
        variant='secondary'
        className='w-full h-10'
        sx={{ paddingX: 2 }}
        onClick={() => vcardInputRef.current?.click()}
        disabled={disabled}
      >
        <IdCard className='mr-2 w-[18px] h-[18px]' strokeWidth={1.5} />
        Share contact card
      </Button>
      {vcardError && (
        <Typography variant='body2' sx={{ color: theme.palette.error.main, lineHeight: 1.4 }}>
          {vcardError}
        </Typography>
      )}

      {pickerSupported && (
        <Button
          variant='secondary'
          className='w-full h-10'
          sx={{ paddingX: 2 }}
          onClick={handlePickContact}
          disabled={disabled}
        >
          <Contact className='mr-2 w-[18px] h-[18px]' strokeWidth={1.5} />
          Use my contact card
        </Button>
      )}

      {/* The host's own one-tap paths, ABOVE the divider with ours (VRG-720). */}
      {quickActions}

      {/* ── ONE DIVIDER, AND IT IS LABELLED ──────────────────────────────────
          The gate offers three ways to supply one pair of values: a contact card,
          Google, and typing. It used to carry a single bare "or" between the
          submit button and the Google button, which grouped the wrong things —
          it read as "typing OR Google" while the contact-card button sat further
          up with no connective tissue at all, so the page looked like it had two
          unrelated halves rather than a fast lane and a fallback.

          Now the split is by EFFORT: every one-tap path above, typing below, one
          boundary between them. The label is what makes it a boundary rather than
          a separator — "or" alone says these are alternatives, "or enter manually"
          says what the alternative costs, which is the thing a visitor is deciding.

          NOT a collapsed section. Hiding the fields behind a disclosure was
          considered and rejected: it puts an extra tap on the fallback at the one
          point in the flow where a visitor is deciding whether to continue at all,
          and the quick paths are platform-dependent — where the contact card is
          unsupported the primary path simply is not there, so everyone would pay
          the extra tap for nothing. Revisit if the contact-card path is ever
          measured to dominate; there is no event sink to measure it with today. */}
      <Stack direction='row' alignItems='center' spacing={1.5} aria-hidden>
        <Box sx={{ flex: 1, height: '1px', backgroundColor: '#E4E4E7' }} />
        <Typography variant='body2' color='text.secondary' sx={{ lineHeight: 1.5 }}>
          or enter manually
        </Typography>
        <Box sx={{ flex: 1, height: '1px', backgroundColor: '#E4E4E7' }} />
      </Stack>

      <Input
        // No `mt` and no brand ring: see RequestAccessInfo's notes on the shared Input.
        sx={{ height: '48px', ...fieldFocusSx(theme) }}
        label='Name'
        placeholder='Enter your name'
        value={name}
        onChange={value => {
          onNameChange(value);
        }}
        onBlur={() => setNameTouched(true)}
        error={!!nameError}
        helperText={nameError}
        disabled={disabled}
        type='text'
        // `autoComplete="name"` is the full-name token: iOS offers the contact card's name
        // for it, and Chrome fills the saved profile name.
        inputProps={{
          ref: nameInputRef,
          autoComplete: 'name',
          name: 'name',
          autoCapitalize: 'words',
          maxLength: CONTACT_NAME_MAX_LENGTH,
        }}
      />

      <Input
        sx={{ height: '48px', ...fieldFocusSx(theme) }}
        label='Email'
        placeholder='Enter your email'
        value={email}
        onChange={value => {
          onEmailChange(value);
        }}
        onBlur={() => setEmailTouched(true)}
        error={!!emailError}
        helperText={emailError}
        disabled={disabled}
        type='email'
        // `type='email'` alone buys the email KEYBOARD. iOS offers the contact-card chip, and
        // Chrome/1Password offer their stored address, only for a field that declares
        // `autocomplete`; `name` is what both fall back to when deciding which field is which.
        inputProps={{
          ref: emailInputRef,
          autoComplete: 'email',
          name: 'email',
          inputMode: 'email',
        }}
      />

      {/*
        The optional three. `Collapse` rather than a bare conditional so the
        section GROWS into place when the name lands or a card is read -- the same
        reason the certificate card's activity strip animates. A block of three
        fields appearing between two frames reads as the form having jumped.

        No `appear`: when the section is already open on first render (a revisit
        with a name, or a card read before mount) it should simply be there. The
        animation belongs to the reveal.

        `unmountOnExit` is off so `Collapse` can measure what it is animating to.
        That is safe here because the fields are controlled by the host's state --
        they hold their values whether or not the section is on screen, which is
        what lets a card fill them and the reveal show them already filled.
      */}
      {optionalSupported && (
        <Collapse in={optionalRevealed} timeout='auto'>
          <Stack spacing={2.5}>
            {/*
              Labelled once for the group rather than "(optional)" on all three.
              Three repetitions of the same word down one column reads as noise,
              and the group has a single answer to the question.
            */}
            <Typography
              variant='body2'
              color='text.secondary'
              sx={{ lineHeight: 1.5 }}
            >
              Optional. Helps the brand know who got in touch.
            </Typography>

            {onCompanyChange && (
              <Input
                sx={{ height: '48px', ...fieldFocusSx(theme) }}
                label='Company'
                placeholder='Where you work'
                value={company}
                onChange={onCompanyChange}
                disabled={disabled}
                type='text'
                inputProps={{
                  autoComplete: 'organization',
                  name: 'organization',
                  maxLength: CONTACT_COMPANY_MAX_LENGTH,
                }}
              />
            )}

            {onTitleChange && (
              <Input
                sx={{ height: '48px', ...fieldFocusSx(theme) }}
                label='Job title'
                placeholder='Your role'
                value={title}
                onChange={onTitleChange}
                disabled={disabled}
                type='text'
                // `organization-title` is the spec token; iOS and Chrome both map
                // the contact card's TITLE to it.
                inputProps={{
                  autoComplete: 'organization-title',
                  name: 'organization-title',
                  maxLength: CONTACT_TITLE_MAX_LENGTH,
                }}
              />
            )}

            {onPhoneChange && (
              <Input
                sx={{ height: '48px', ...fieldFocusSx(theme) }}
                label='Phone'
                placeholder='Your phone number'
                value={phone}
                onChange={onPhoneChange}
                disabled={disabled}
                // `type='tel'` for the keypad; no validation, because a correct
                // international phone regex does not exist and a wrong one
                // rejects real numbers on a field nobody is required to fill.
                type='tel'
                inputProps={{
                  autoComplete: 'tel',
                  name: 'tel',
                  inputMode: 'tel',
                  maxLength: CONTACT_PHONE_MAX_LENGTH,
                }}
              />
            )}
          </Stack>
        </Collapse>
      )}
    </Stack>
  );
};
